import { useContext, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Form, Input, Radio, notification } from "antd";
import { Rule } from "antd/es/form";
import classNames from "classnames";
import { selectClientRoles, selectAssessmentRoles } from "@/shared/services/store/rolesStore";
import { useAssessmentHook } from "@/shared/hooks/assessments/useAssessment.hook";
import { useGetClientUsersHook } from "@/shared/hooks/users/use-get-client-users.hook";
import { SubmitButton } from "@/ui-components/commonComponents/Button/SubmitButton";
import buttonStyle from "@/ui-components/commonComponents/Button/style.module.css";
import { CustomInput, InputType } from "@/ui-components/commonComponents/Input/Input";
import { SEO } from "@/ui-components/commonComponents/SEO/SEO";
import { AssessmentAbilityContext } from "@/shared/contexts/ability.context";
import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";
import { Loading } from "@/shared/components/Loading/Loading";
import { ASSESSMENT_ROUTE, CLIENT_ROUTE, SETTINGS_ROUTE, USER_ROUTE } from "@/routing/AppRouter/routes.constants";
import { IUser } from "@/modules/client/clients.interface";
import { IClientUserRole } from "@/shared/services/roles/roles-http.service";
import { ClientRoleEnum } from "@/modules/client/client-roles.interface";
import {
  AssessmentRoleEnum,
  assessmentRoleDefinitions,
  IAssessmentPermissions,
} from "@/modules/assessments/services/assessment-permissions-http.service";
import {
  getAssessmentRoleOptions,
  IAssessmentRoleOption,
} from "@/modules/assessments/scenes/AssessmentTeam/assessment-roles.utils";
import { useManageAssessmentUserHook } from "@/modules/assessments/hooks/use-manage-assessment-user.hook";
import { useGetAssessmentUsersPermissionsHook } from "@/modules/assessments/hooks/use-get-assessment-users-permissions.hook";
import { ClientHttpService } from "@/modules/client/services/client-http.service";
import { getFriendlyErrorMessage } from "@/shared/services/http";
import { ViewRolesModal } from "@/shared/components/ViewRolesModal/ViewRolesModal";
import style from "@/shared/components/AddNew/style.module.css";
import styles from "./add-user-to-assessment.styles.module.css";
import { DEFAULT_LOCALE } from "@/shared/utils/locale.utils";

export function getAddUserToAssessmentPageLink(clientId: number, assessmentId: number) {
  return `/${CLIENT_ROUTE}/${clientId}/${ASSESSMENT_ROUTE}/${assessmentId}/${USER_ROUTE}`;
}

interface IFormInputProps {
  name: string;
  label: string;
  inputType: InputType;
  selectOptions?: any[];
  rules: Rule[];
  isRoleField?: boolean;
}

interface IFormSubmission {
  assessmentRole: AssessmentRoleEnum;
  email: string;
  fullName: string;
  isExistingUser: boolean;
  user: string;
}

const getExistingUserInputProps = ({
  clientUsers,
  assessmentPermissions,
  clientRoles,
  assessmentRoleOptions,
}: {
  clientUsers: IUser[];
  assessmentPermissions: IAssessmentPermissions[];
  clientRoles: IClientUserRole[];
  assessmentRoleOptions: IAssessmentRoleOption[];
}): IFormInputProps[] => {
  // check if not global admin & not client admin & have not existed assessment role
  const users = clientUsers
    .filter(({ userId, isAdmin, clientUsers }) => {
      const existedAssessmentPermissions = assessmentPermissions.find(
        (assessmentPermission) => assessmentPermission.userId === userId,
      );
      const clientUserRole = clientRoles.find(({ roleId }) => clientUsers && clientUsers[0].roleId === roleId);
      const isClientUserAdmin = clientUserRole && clientUserRole.name === ClientRoleEnum.ADMIN;

      return !existedAssessmentPermissions && !isClientUserAdmin && !isAdmin;
    })
    .map((user) => ({
      value: String(user.userId),
      name: user.fullName,
    }));

  return [
    {
      name: "user",
      label: "Select User",
      inputType: InputType.select,
      selectOptions: users,
      rules: [
        {
          type: "string",
          required: true,
          message: "Please select a valid user",
        },
      ],
    },
    {
      name: "assessmentRole",
      label: "Select Assessment Role",
      inputType: InputType.select,
      selectOptions: assessmentRoleOptions,
      rules: [
        {
          type: "string",
          required: true,
          message: "Please select a valid assessment role",
        },
      ],
      isRoleField: true,
    },
  ];
};

const newUserInputProps: IFormInputProps[] = [
  {
    name: "fullName",
    label: "Full Name",
    inputType: InputType.input,
    rules: [
      {
        type: "string",
        required: true,
        message: "Please enter a valid name",
      },
    ],
  },
  {
    name: "email",
    label: "Email",
    inputType: InputType.input,
    rules: [
      {
        type: "email",
        required: true,
        message: "Please enter a valid email",
      },
    ],
  },
];

export function AddUserToAssessmentComponent() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRolesModalOpen, setIsRolesModalOpen] = useState(false);
  const { client_id, assessment_id } = useParams<{ client_id: string; assessment_id: string }>();
  const assessmentId = Number(assessment_id);
  const settingsUrl = `/${CLIENT_ROUTE}/${client_id}/${ASSESSMENT_ROUTE}/${assessment_id}/${SETTINGS_ROUTE}`;
  const clientRoles = useSelector(selectClientRoles);
  const assessmentRoles = useSelector(selectAssessmentRoles);
  const assessmentRoleOptions = getAssessmentRoleOptions(assessmentRoles);

  const navigate = useNavigate();
  const [form] = Form.useForm();

  // TODO: Move to global state
  const { assessment, isLoading: isLoadingAssessment } = useAssessmentHook(assessmentId);

  const { users: clientUsers, isLoading: isLoadingClientUsers } = useGetClientUsersHook(assessment?.clientId);
  const { assessmentPermissions, isLoading: isLoadingAssessmentPermissions } =
    useGetAssessmentUsersPermissionsHook(assessmentId);
  const { manageAssessmentUser, isLoading: isLoadingUserToAssessment } = useManageAssessmentUserHook();

  const navigateToSettings = () => {
    navigate(settingsUrl);
  };

  const addUserAndNavigateBack = async ({ isExistingUser, user, assessmentRole, fullName, email }: IFormSubmission) => {
    try {
      setIsSubmitting(true);
      if (isExistingUser) {
        await manageAssessmentUser(assessmentId, Number(user), assessmentRole);
      } else {
        await ClientHttpService.addUserToClient(String(assessment?.clientId), {
          email: email,
          fullName: fullName,
          assessmentId: assessmentId,
          assessmentRole: assessmentRole,
        });
      }
      setIsSubmitting(false);
      notification.success({ message: "User added successfully." });

      setTimeout(() => {
        navigateToSettings();
      }, 3000);
    } catch (error) {
      setIsSubmitting(false);
      notification.error({
        message: getFriendlyErrorMessage(error) ?? "Oops, something went wrong. Please try again.",
      });
    }
  };

  const assessmentAbility = useContext(AssessmentAbilityContext);
  const canCreateAssessmentUser = assessmentAbility.can(Action.Create, Subject.ASSESSMENT_USER_PERMISSION);
  const existingUserInputProps = getExistingUserInputProps({
    clientUsers,
    assessmentPermissions,
    clientRoles,
    assessmentRoleOptions,
  });

  if (isLoadingAssessment || isLoadingUserToAssessment || isLoadingClientUsers || isLoadingAssessmentPermissions) {
    return <Loading />;
  }

  return (
    <div className={styles.background}>
      <SEO titleParams={[assessment?.name[DEFAULT_LOCALE], "Add Team Member"]} />
      <div className={styles.mainContainer}>
        <div className={styles.formContainer}>
          <h1>Add an Assessment Team Member</h1>
          <Form
            form={form}
            name="AssessmentUser"
            requiredMark={false}
            colon={false}
            onFinish={addUserAndNavigateBack}
            initialValues={{
              assessmentRole: AssessmentRoleEnum.HIRING_MANAGER,
              email: null,
              fullName: null,
              isExistingUser: true,
              user: null,
            }}
          >
            <Form.Item name="isExistingUser">
              <Radio.Group>
                <Radio value={true}>Add an Existing User</Radio>
                <Radio value={false}>Add a New User</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.isExistingUser !== currentValues.isExistingUser}
            >
              {({ getFieldValue }) => {
                const isExistingUser = getFieldValue("isExistingUser");
                return (
                  <>
                    {isExistingUser ? (
                      existingUserInputProps.map(({ name, label, rules, selectOptions, isRoleField, ...input }) => (
                        <Form.Item
                          className={classNames(style.inputTitle, styles.userAssessmentInput)}
                          key={name}
                          name={name}
                          label={
                            isRoleField ? (
                              <div className={styles.labelRow}>
                                <span>{label}</span>
                                <button
                                  type="button"
                                  className={styles.viewRoles}
                                  onClick={() => setIsRolesModalOpen(true)}
                                >
                                  View Roles
                                </button>
                              </div>
                            ) : (
                              label
                            )
                          }
                          rules={rules}
                        >
                          <CustomInput
                            placeholder="Select..."
                            className={styles.selectInput}
                            selectOptions={selectOptions}
                            inputType={input.inputType}
                          />
                        </Form.Item>
                      ))
                    ) : (
                      <>
                        {newUserInputProps.map(({ name, label, rules }) => (
                          <Form.Item
                            className={classNames(style.inputTitle, styles.userAssessmentInput)}
                            key={name}
                            name={name}
                            label={label}
                            rules={rules}
                          >
                            <Input placeholder="Type..." />
                          </Form.Item>
                        ))}
                        {
                          <Form.Item
                            className={classNames(style.inputTitle, styles.userAssessmentInput)}
                            name="assessmentRole"
                            label={
                              <div className={styles.labelRow}>
                                <span>Select Assessment Role</span>
                                <button
                                  type="button"
                                  className={styles.viewRoles}
                                  onClick={() => setIsRolesModalOpen(true)}
                                >
                                  View Roles
                                </button>
                              </div>
                            }
                            rules={[
                              {
                                type: "string",
                                required: true,
                                message: "Please select a valid Assessment Role",
                              },
                            ]}
                          >
                            <CustomInput
                              placeholder="Select..."
                              className={styles.selectInput}
                              selectOptions={assessmentRoleOptions}
                              inputType={InputType.select}
                            />
                          </Form.Item>
                        }
                      </>
                    )}

                    <div className={classNames(style.buttonContainer, styles.buttonsContainer)}>
                      <SubmitButton isLoading={isSubmitting} disabled={!canCreateAssessmentUser}>
                        {isExistingUser ? "Add" : "Send Invite"}
                      </SubmitButton>

                      <Link
                        to={settingsUrl}
                        className={classNames(buttonStyle.linkAsButton, buttonStyle.linkAsButtonTransparent)}
                      >
                        Cancel
                      </Link>
                    </div>
                  </>
                );
              }}
            </Form.Item>
          </Form>
        </div>
      </div>
      <ViewRolesModal
        isOpen={isRolesModalOpen}
        onClose={() => setIsRolesModalOpen(false)}
        title="Assessment Roles"
        roles={assessmentRoleDefinitions}
      />
    </div>
  );
}
